/**
 * ExpendableSkillEffect — Conjuration Swarm path passive: early-death explosion.
 *
 * Always-on passive. Any wisp combat-killed within 1 second of spawning
 * automatically detonates for 5 damage in a 1-tile radius at the death
 * position. Late deaths (> earlyDeathWindowMs) do not trigger.
 *
 * Mechanic:
 * - `trackSpawn(wispId, spawnTimeMs)` — called by integration layer when any
 *   wisp spawns (Swarm or Overwhelm). Records spawn timestamp keyed by wisp ID.
 * - `onWispDied(wispId, deathCause, x, y, currentTimeMs)` — called on wisp death.
 *   If deathCause === 'combat' and age <= earlyDeathWindowMs, fires AoE explosion
 *   via `combat.applyAoEDamage()` at the death position.
 * - This fires for all Swarm-path wisps regardless of which skill spawned them.
 *
 * UpgradeEffectResolver keys (all tree-scoped to 'conjuration'):
 * - earlyDeathExplosionDamage: additive, base 5
 * - earlyDeathExplosionRadiusTiles: additive, base 1
 * - earlyDeathWindowMs: max, base 1000ms
 *
 * Does NOT use DeathTriggerSystem (wisps are not enemies). Does NOT fire on
 * Detonate kills (deathCause: 'detonate') or natural expiry.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Expendable
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ExpendableDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly combat: {
    /**
     * Apply AoE damage centered at (x, y) within radiusTiles radius.
     * Called by integration layer which routes to CombatHitResolver.
     */
    applyAoEDamage(x: number, y: number, radiusTiles: number, damage: number): void;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface ExpendableConfig {
  readonly earlyDeathExplosionDamage: number;
  readonly earlyDeathExplosionRadiusTiles: number;
  readonly earlyDeathWindowMs: number;
}

const DEFAULT_CONFIG: ExpendableConfig = {
  earlyDeathExplosionDamage: 5,      // spec § Expendable
  earlyDeathExplosionRadiusTiles: 1, // 1-tile radius AoE
  earlyDeathWindowMs: 1000,          // 1-second early-death window
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ExpendableSkillEffect implements SkillEffect {
  readonly skillId = 'swarm_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: ExpendableDeps;
  private readonly config: ExpendableConfig;

  /** Spawn timestamp (ms) keyed by wisp ID. */
  private readonly _spawnTimes = new Map<string, number>();

  // Observable state for scenarios
  private _explosionCount = 0;
  private _lastExplosionX = 0;
  private _lastExplosionY = 0;
  private _lastExplosionDamage = 0;
  private _lastExplosionRadiusTiles = 0;

  constructor(deps: ExpendableDeps, config: Partial<ExpendableConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Expendable-specific API ---

  /**
   * Record the spawn time of a wisp. Called by integration layer when any
   * Swarm-path wisp spawns (Swarm, Overwhelm, Becomes the Many, etc.).
   */
  trackSpawn(wispId: string, spawnTimeMs: number): void {
    this._spawnTimes.set(wispId, spawnTimeMs);
  }

  /**
   * Handle wisp death. Fires early-death explosion if conditions are met.
   *
   * @param wispId     The dying wisp's ID (must have been registered via trackSpawn)
   * @param deathCause 'combat' | 'detonate' | 'expiry'
   * @param x          Death position X (world pixels)
   * @param y          Death position Y (world pixels)
   * @param currentTimeMs  Current simulation time in ms
   */
  onWispDied(
    wispId: string,
    deathCause: 'combat' | 'detonate' | 'expiry' | 'converge',
    x: number,
    y: number,
    currentTimeMs: number,
  ): void {
    // Only fires on combat kills
    if (deathCause !== 'combat') return;

    const spawnTime = this._spawnTimes.get(wispId);
    if (spawnTime === undefined) return; // untracked wisp

    // Resolve early-death window
    const windowOverride = this.deps.resolver.getMaxEffect('swarm_path_passive', 'earlyDeathWindowMs');
    const windowMs = windowOverride !== undefined
      ? Math.max(this.config.earlyDeathWindowMs, windowOverride)
      : this.config.earlyDeathWindowMs;

    const age = currentTimeMs - spawnTime;
    if (age > windowMs) return; // outside early-death window

    // Resolve explosion parameters
    const damageBonus = this.deps.resolver.getAdditiveEffect('swarm_path_passive', 'earlyDeathExplosionDamage');
    const radiusBonus = this.deps.resolver.getAdditiveEffect('swarm_path_passive', 'earlyDeathExplosionRadiusTiles');

    const resolvedDamage = this.config.earlyDeathExplosionDamage + damageBonus;
    const resolvedRadius = this.config.earlyDeathExplosionRadiusTiles + radiusBonus;

    this.deps.combat.applyAoEDamage(x, y, resolvedRadius, resolvedDamage);

    this._explosionCount++;
    this._lastExplosionX = x;
    this._lastExplosionY = y;
    this._lastExplosionDamage = resolvedDamage;
    this._lastExplosionRadiusTiles = resolvedRadius;

    this._spawnTimes.delete(wispId);
  }

  // --- State getters ---

  get explosionCount(): number { return this._explosionCount; }
  get lastExplosionX(): number { return this._lastExplosionX; }
  get lastExplosionY(): number { return this._lastExplosionY; }
  get lastExplosionDamage(): number { return this._lastExplosionDamage; }
  get lastExplosionRadiusTiles(): number { return this._lastExplosionRadiusTiles; }
  get trackedWispCount(): number { return this._spawnTimes.size; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // passive — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Passive — no frame logic
  }

  destroy(): void {
    this._spawnTimes.clear();
  }
}
